import Analytics from "analytics";
import type { AnalyticsInstance } from "analytics";
import { type z } from "zod";

import type { EventName, Events } from "./events";
import { posthogPlugin } from "./plugins/posthog";

interface CaptureEventOptions<T extends EventName> {
  eventName: T;
  eventData: z.infer<(typeof Events)[T]>;
}

interface IdentifyOptions {
  userId: string;
  displayName: string;
  email?: string | null;
  role?: string | null;
}

interface TelemetryConstructorOptions {
  appName: "chainpatrol-web";
  posthogApiKey: string;
}

export class Telemetry {
  private client: AnalyticsInstance;

  constructor({ appName, posthogApiKey }: TelemetryConstructorOptions) {
    this.client = Analytics({
      app: appName,
      plugins: [
        posthogPlugin({
          enabled: false,
          token: posthogApiKey,
          options: {
            debug: false,
            autocapture: false,
            api_host: "/ingest/posthog",
            ui_host: "https://us.posthog.com",
            loaded: (posthog) => {
              if (process.env.NODE_ENV === "development") {
                posthog.debug();
              }
            },
          },
        }),
      ],
    });
  }

  async captureEvent<T extends EventName>({
    eventName,
    eventData,
  }: CaptureEventOptions<T>) {
    await this.client.track(eventName, eventData);
  }

  async enablePosthog() {
    await this.client.plugins.enable("posthog");
  }

  isPosthogEnabled() {
    return this.client.getState()?.posthog?.enabled;
  }

  async page() {
    await this.client.page();
  }

  async identify({ userId, displayName, email, role }: IdentifyOptions) {
    await this.client.identify(userId, { displayName, email, role });
  }
}
